import React, { useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { fromJS } from 'immutable';
import { Button, CircularProgress } from '@mui/material';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from 'lodash';
import { FilterAltOutlined } from '@mui/icons-material';

import DownloadIcon from 'assets/icons/download.svg';
import Api from 'entities/api';
import config from 'config';
import { usePayrollRunFlatFileQuery } from 'generated/graphql';
import FormSelectBoxEntitySelector from 'retired/FormSelectBoxEntitySelector';

import { DonationsTable } from './DonationsTable';
import PayrollRunHeader from './PayrollRunHeader';

type Props = {
  match: { params: { id: string; paygroupId: string } };
};

export interface PayrollRun {
  id: string;
  startDate: string;
  endDate: string;
  startDateExternal?: string;
  endDateExternal?: string;
  payDate: string;
  stats: {
    isDonatable: boolean;
    totalUsd: number;
    donationCount?: number | null;
  };
}

export interface DonationTableFilters {
  paymentStatuses: string[] | undefined | null;
  frequency: string | undefined | null;
  employeeTerm: string | undefined | null;
  nonprofitTerm: string | undefined | null;
  payrollPayGroupId: string;
}

export const ExportLink = ({
  payrollRunId,
  filters,
}: {
  payrollRunId: string;
  filters: DonationTableFilters;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events
    <a
      style={{ outline: 'none' }}
      onClick={async (event) => {
        if (isLoading) {
          return;
        }
        setIsLoading(true);
        const a = event.target.tagName === 'A' ? event.target : event.target.parentElement;
        if (!a.href) {
          const response = await fetch(`${config.payrollApiUrl}/payroll/run/file/`, {
            method: 'POST',
            body: JSON.stringify({ payrollRunId, ...filters }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: Api.defaultHeaders.Authorization,
            },
          });

          const blob = await response.blob();

          const url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download =
            response.headers.get('content-disposition')?.replace('attachment; filename=', '') ||
            'deed.csv';
          a.click();
          window.URL.revokeObjectURL(url);
          a.removeAttribute('href');
          setIsLoading(false);
        }
      }}
      role="button"
      tabIndex={0}
      title="Exports"
    >
      {isLoading ? (
        <CircularProgress size={25} />
      ) : (
        <img alt="Download" src={DownloadIcon} width={30} />
      )}
    </a>
  );
};

export const PayrollRunView = ({ match }: Props) => {
  const [paymentStatuses, setPaymentStatuses] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [employeeTermToSubmit, setEmployeeTermToSubmit] = useState<string | null>('');
  const [nonprofitTermToSubmit, setNonprofitTermToSubmit] = useState<string | null>('');

  const searchNonprofitInputRef = useRef<HTMLInputElement>();
  const searchEmployeeInputRef = useRef<HTMLInputElement>();

  const payrollRunId = match.params.id;
  const paygroupIdParam = match.params.paygroupId;
  const { data, loading } = usePayrollRunFlatFileQuery({
    variables: { id: payrollRunId, payrollPayGroupId: paygroupIdParam },
  });

  const payrollRunFlatFile = data?.payrollRunFlatFile;
  const payrollPayGroup = data?.payrollPayGroup;

  const paymentStatusesOptions = fromJS(
    [
      { id: 'Pending', name: 'Pending' },
      { id: 'Success', name: 'Success' },
      { id: 'Failed', name: 'Failed' },
      { id: 'Cancelled', name: 'Cancelled' },
      { id: 'Unresolved', name: 'Unresolved' },
    ].map((option) => ({
      id: `${option.id}`,
      name: option.name,
    }))
  );
  const frequencyOptions = fromJS(
    [
      { id: 'One-Time', name: 'One-Time' },
      { id: 'Every PayCheck', name: 'Every PayCheck' },
    ].map((option) => ({
      id: `${option.id}`,
      name: option.name,
    }))
  );

  const handleSearchEmployeeDebounced = debounce(
    (search: string) => setEmployeeTermToSubmit(search),
    600
  );
  const handleSearchNonprofitDebounced = debounce(
    (search: string) => setNonprofitTermToSubmit(search),
    600
  );

  const clearFilters = () => {
    setPaymentStatuses([]);
    setFrequency(null);
    setEmployeeTermToSubmit('');
    setNonprofitTermToSubmit('');
    if (searchNonprofitInputRef.current && searchEmployeeInputRef.current) {
      searchNonprofitInputRef.current.value = '';
      searchEmployeeInputRef.current.value = '';
    }
  };

  return (
    <>
      {loading && <LinearProgress />}
      {payrollRunFlatFile && (
        <PayrollRunHeader
          payrollRun={payrollRunFlatFile}
          paygroup={paygroupIdParam}
          title={payrollPayGroup?.payGroupDisplayName || ''}
        />
      )}
      <div
        style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'space-between',
          margin: '20px 0px',
        }}
      >
        <div style={{ display: 'flex', gap: '20px' }}>
          <FormSelectBoxEntitySelector
            multiple
            options={paymentStatusesOptions}
            placeholder="Payment status"
            allowClear
            input={{
              onChange: (values: string[]) => setPaymentStatuses([...values]),
              value: paymentStatuses,
            }}
          />

          <FormSelectBoxEntitySelector
            options={frequencyOptions}
            placeholder="Frequency"
            allowClear
            input={{
              onChange: (values: string[]) => setFrequency(values),
              value: frequency,
            }}
          />

          <TextField
            type="search"
            size="small"
            placeholder="Search employee"
            inputRef={searchEmployeeInputRef}
            // value={employeeTerm}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear"
                    onClick={(): void => {
                      if (searchEmployeeInputRef.current) {
                        searchEmployeeInputRef.current.value = '';
                      }
                      setEmployeeTermToSubmit('');
                    }}
                    size="small"
                    disabled={false}
                  >
                    <ClearIcon fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              handleSearchEmployeeDebounced(event.target.value)
            }
            style={{
              width: 200,
              marginLeft: 0,
            }}
          />

          <TextField
            type="search"
            size="small"
            placeholder="Search nonprofits"
            inputRef={searchNonprofitInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear"
                    onClick={(): void => {
                      if (searchNonprofitInputRef.current) {
                        searchNonprofitInputRef.current.value = '';
                      }
                      setNonprofitTermToSubmit('');
                    }}
                    size="small"
                    disabled={false}
                  >
                    <ClearIcon fontSize="inherit" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            // value={nonprofitTerm}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              handleSearchNonprofitDebounced(event.target.value)
            }
            style={{
              width: 200,
              marginLeft: 0,
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '20px', margin: '0px 20px' }}>
          {payrollRunFlatFile && payrollPayGroup ? (
            <ExportLink
              size={20}
              payrollRunId={payrollRunFlatFile.id}
              filters={{
                paymentStatuses,
                frequency,
                employeeTerm: employeeTermToSubmit,
                nonprofitTerm: nonprofitTermToSubmit,
                payrollPayGroupId: payrollPayGroup.id,
              }}
            />
          ) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              clearFilters();
            }}
            startIcon={<FilterAltOutlined />}
          >
            Clear filters
          </Button>
        </div>
      </div>
      {payrollRunFlatFile && payrollPayGroup && (
        <DonationsTable
          filters={{
            paymentStatuses,
            frequency,
            employeeTerm: employeeTermToSubmit,
            nonprofitTerm: nonprofitTermToSubmit,
            payrollPayGroupId: payrollPayGroup.id,
          }}
          payrollRunFlatFileId={payrollRunFlatFile.id}
        />
      )}
    </>
  );
};

export default PayrollRunView;
